import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useCallback } from "react";
import { fetchUserAPI, User } from "../../../../api/userApi";
import { useCommon } from "../../../../context/MyContext";

/**
 * 사용자 정보를 관리하는 React Query 훅
 *
 * - tanstack-query로 user 데이터 캐싱 및 관리
 * - MyContext 자동 동기화 (레거시 호환성)
 * - 로그인 상태 확인 및 refetch 기능 제공
 * - tone 로컬 오버라이드 관리
 *
 * @note 이 훅은 React Query를 사용하며, MyContext 동기화를 포함합니다.
 *       향후 MyContext가 제거되면 동기화 로직도 함께 제거될 예정입니다.
 *
 * @returns user, isLoggedIn, refetchUser, isLoading, error, setUser, resetAuth
 */
export const useUserQuery = () => {
  const queryClient = useQueryClient();
  const { setUser: setContextUser, setIsLogin: setContextIsLogin } =
    useCommon();

  const {
    data: user,
    refetch,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["user"],
    queryFn: fetchUserAPI,
    refetchOnMount: true, // 컴포넌트 마운트 시 자동 refetch
  });

  // setUser: 사용자 정보 부분 업데이트 (tone 등 로컬 상태 변경 시 사용)
  const setUser = useCallback(
    (userData: Partial<User>) => {
      queryClient.setQueryData(["user"], (oldData: User | undefined) => {
        if (!oldData) return oldData;
        const updated = { ...oldData, ...userData };
        // MyContext도 동기화
        setContextUser(updated);
        return updated;
      });
    },
    [queryClient, setContextUser]
  );

  // resetAuth: 인증 상태 초기화 (로그아웃 시 사용)
  const resetAuth = useCallback(() => {
    queryClient.setQueryData(["user"], null);
    queryClient.removeQueries({ queryKey: ["user"] });
  }, [queryClient]);

  // user 데이터가 변경되면 MyContext 동기화 (레거시 호환)
  useEffect(() => {
    if (user) {
      // MyContext 업데이트 (레거시 코드 호환)
      // TODO: 레거시 페이지 마이그레이션 후 제거
      setContextUser(user);
      setContextIsLogin(false); // 로그인 모달 닫기
    } else {
      // 로그아웃 상태
      const emptyUser: User = {
        id: null,
        name: null,
        tone: null,
        picture_extension: null,
        picture_data: null,
        paid: false,
        start_dt: null,
        end_dt: null,
        tutor_cnt: 0,
        socra_cnt: 0,
      };

      setContextUser(emptyUser);
      setContextIsLogin(false);
    }
  }, [user, setContextUser, setContextIsLogin]);

  return {
    user: user || null,
    isLoggedIn: !!user?.id,
    refetchUser: refetch,
    isLoading,
    error,
    setUser,
    resetAuth,
  };
};
